import { FC, useMemo } from 'react';
import styles from './StudyPeriodSection.module.scss';
import { CardValue, StudyInfoCard, StudyInfoPageSection } from '../../layoutComponents/StudyInfoPageLayout';
import { useStudyClosing } from 'src/features/study/useStudyClosing';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { formatDateRange } from 'src/helpers/date-helpers';
import { addDays, formatDuration, intervalToDuration } from 'date-fns';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { useTranslation } from 'react-i18next';
import { getCurrentDateLocale } from 'src/application/localisation/localization';

export const StudyPeriodSection = () => {
  const { t } = useScopedTranslation('StudyInfoPage.StudyPeriodSection');
  const { t: tCommon } = useTranslation();
  const study = useStudy();
  const { buttonComponent: closeStudyButtonComponent, cardComponent: closeStudyCardComponent } = useStudyClosing();

  const plannedCloseAt = useMemo(() => study && addDays(study.startDate, study.duration), [study]);
  const timeLeft = useMemo(
    () =>
      plannedCloseAt &&
      intervalToDuration({
        start: new Date(),
        end: plannedCloseAt,
      }),
    [plannedCloseAt],
  );
  const studyIsClosed = useMemo(() => plannedCloseAt!.getTime() < new Date().getTime(), [plannedCloseAt]);
  const duration: Duration | undefined = useMemo(
    () =>
      !study
        ? undefined
        : plannedCloseAt && study.startDate
        ? intervalToDuration({
            start: study.startDate,
            end: plannedCloseAt,
          })
        : { days: study.duration },
    [plannedCloseAt, study],
  );

  return (
    <StudyInfoPageSection
      title={t('Title')}
      variant={'flex'}
      rightHeader={formatDateRange(study?.startDate, study?.closingApprovedAt ?? plannedCloseAt)}
    >
      <StudyInfoCard title={t('Duration')}>
        <div className={styles.durationSection}>
          <CardPeriodValue duration={duration} periodType={'years'} />
          <CardPeriodValue duration={duration} periodType={'months'} />
          <CardPeriodValue duration={duration} periodType={'days'} />
        </div>
      </StudyInfoCard>
      {closeStudyCardComponent ?? (
        <StudyInfoCard
          title={
            <div className={styles.cardTitle}>
              <div>{studyIsClosed ? t('TimeIsUp') : t('TimeLeft')}</div>
              {closeStudyButtonComponent}
            </div>
          }
        >
          {studyIsClosed ? (
            <CardValue mainText={tCommon('Common_dash')} />
          ) : (
            <div className={styles.durationSection}>
              <CardPeriodValue duration={timeLeft} periodType={'years'} />
              <CardPeriodValue duration={timeLeft} periodType={'months'} />
              <CardPeriodValue duration={timeLeft} periodType={'days'} />
              {!timeLeft?.days && (
                <>
                  <CardPeriodValue duration={timeLeft} periodType={'hours'} />
                  <CardPeriodValue duration={timeLeft} periodType={'minutes'} />
                </>
              )}
            </div>
          )}
        </StudyInfoCard>
      )}
    </StudyInfoPageSection>
  );
};

const CardPeriodValue: FC<{
  duration: Duration | undefined;
  periodType: keyof Duration;
}> = ({ duration, periodType }) => {
  if (!duration?.[periodType]) return <></>;

  return (
    <CardValue
      mainText={duration[periodType]!.toString()}
      secondText={formatDuration(
        { [periodType]: duration[periodType] },
        {
          locale: getCurrentDateLocale().locale,
        },
      ).replace(/[0-9\s]*/g, '')}
    />
  );
};
